import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { MaskedLine, Reveal, Eyebrow } from "@/components/Reveal";
import { SITE, INTERESTS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full border border-white/10 bg-white/[0.03] px-5 py-4 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-300 focus:border-gold/60";

export default function Contact() {
    const [params] = useSearchParams();
    const initialInterest = INTERESTS.includes(params.get("interest"))
        ? params.get("interest")
        : "General Enquiry";

    const [form, setForm] = useState({ name: "", email: "", interest: initialInterest, message: "" });
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        setError("");
        try {
            const res = await fetch(`${API}/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!res.ok) throw new Error("Request failed");
            setStatus("success");
        } catch {
            setStatus("error");
            setError("Something went wrong sending your enquiry. Please try again, or email us directly.");
        }
    };

    return (
        <section data-testid="contact-page" className="relative overflow-hidden">
            <div className="pointer-events-none absolute -top-40 left-[-10%] h-[30rem] w-[30rem] rounded-full bg-[#D4AF37]/10 blur-3xl" />
            <div className="mx-auto grid max-w-7xl gap-20 px-6 pt-40 pb-28 md:px-10 md:pt-48 md:pb-40 lg:grid-cols-12">
                <div className="lg:col-span-6">
                    <MaskedLine delay={0.05}>
                        <Eyebrow>Contact the desk</Eyebrow>
                    </MaskedLine>
                    <h1 className="mt-8 font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-balance">
                        <MaskedLine delay={0.2}>Let's talk about</MaskedLine>
                        <MaskedLine delay={0.34}>
                            your <span className="font-editorial italic font-light text-gold">next position.</span>
                        </MaskedLine>
                    </h1>
                    <Reveal delay={0.55} className="mt-10 max-w-lg">
                        <p className="text-base md:text-lg leading-relaxed text-zinc-400">
                            Bots, signals, education or partnership — every enquiry lands directly with the
                            research desk. Expect a considered reply, usually within one business day.
                        </p>
                    </Reveal>
                    <Reveal delay={0.7} className="mt-14 space-y-10">
                        <div>
                            <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">Email</h3>
                            <a
                                href={`mailto:${SITE.email}`}
                                data-testid="contact-email-link"
                                className="mt-3 inline-flex items-center gap-2 font-display text-xl font-bold text-gold hover:text-gold-light transition-colors duration-300"
                            >
                                {SITE.email} <ArrowUpRight size={17} />
                            </a>
                        </div>
                        <div>
                            <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                                Registered office
                            </h3>
                            <address data-testid="contact-address" className="mt-3 not-italic text-base leading-relaxed text-zinc-300">
                                {SITE.address.map((line) => (
                                    <span key={line} className="block">{line}</span>
                                ))}
                            </address>
                        </div>
                        <div>
                            <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                                Coverage
                            </h3>
                            <p className="mt-3 text-base text-zinc-300">Commodities · Stocks · ETFs</p>
                        </div>
                    </Reveal>
                </div>

                <Reveal delay={0.4} className="lg:col-span-6">
                    <div className="border border-white/10 bg-coal/40 p-8 md:p-12">
                        {status === "success" ? (
                            <div data-testid="contact-success" className="flex min-h-[420px] flex-col items-start justify-center">
                                <CheckCircle2 size={40} className="text-gold" />
                                <h2 className="mt-8 font-display text-3xl font-extrabold tracking-tight">
                                    Enquiry received.
                                </h2>
                                <p className="mt-4 max-w-sm text-sm leading-relaxed text-zinc-400">
                                    Thank you, {form.name.split(" ")[0]}. Your message is with the desk —
                                    we will reply to {form.email} shortly.
                                </p>
                                <button
                                    data-testid="contact-send-another"
                                    onClick={() => {
                                        setForm({ name: "", email: "", interest: "General Enquiry", message: "" });
                                        setStatus("idle");
                                    }}
                                    className="mt-10 rounded-full border border-white/20 px-8 py-3 text-sm text-white transition-colors duration-300 hover:bg-white hover:text-black"
                                >
                                    Send another enquiry
                                </button>
                            </div>
                        ) : (
                            <form data-testid="contact-form" onSubmit={onSubmit} className="space-y-6">
                                <div>
                                    <label htmlFor="contact-name" className="mb-2.5 block font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                                        Full name
                                    </label>
                                    <input
                                        id="contact-name"
                                        data-testid="contact-name-input"
                                        name="name"
                                        required
                                        minLength={2}
                                        value={form.name}
                                        onChange={onChange}
                                        placeholder="Amara Chen"
                                        className={inputCls}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="contact-email" className="mb-2.5 block font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                                        Email address
                                    </label>
                                    <input
                                        id="contact-email"
                                        data-testid="contact-email-input"
                                        name="email"
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={onChange}
                                        placeholder="you@example.com"
                                        className={inputCls}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="contact-interest" className="mb-2.5 block font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                                        I'm interested in
                                    </label>
                                    <select
                                        id="contact-interest"
                                        data-testid="contact-interest-select"
                                        name="interest"
                                        value={form.interest}
                                        onChange={onChange}
                                        className={`${inputCls} appearance-none bg-obsidian`}
                                    >
                                        {INTERESTS.map((i) => (
                                            <option key={i} value={i} className="bg-obsidian">
                                                {i}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="contact-message" className="mb-2.5 block font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                                        Message
                                    </label>
                                    <textarea
                                        id="contact-message"
                                        data-testid="contact-message-input"
                                        name="message"
                                        required
                                        minLength={10}
                                        rows={5}
                                        value={form.message}
                                        onChange={onChange}
                                        placeholder="Tell us about your trading goals, experience level, and what you'd like from Pocketfolio."
                                        className={`${inputCls} resize-none`}
                                    />
                                </div>
                                {status === "error" && (
                                    <p data-testid="contact-error" className="flex items-center gap-2 text-sm text-red-400">
                                        <AlertTriangle size={15} /> {error}
                                    </p>
                                )}
                                <button
                                    type="submit"
                                    data-testid="contact-submit-button"
                                    disabled={status === "sending"}
                                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-10 py-4 text-sm font-medium text-black transition-colors duration-300 hover:bg-gold-light disabled:opacity-60"
                                >
                                    {status === "sending" ? (
                                        <>
                                            <Loader2 size={16} className="animate-spin" /> Sending…
                                        </>
                                    ) : (
                                        <>
                                            Send enquiry <ArrowUpRight size={16} />
                                        </>
                                    )}
                                </button>
                                <p className="text-center font-mono text-[11px] text-zinc-600">
                                    Your details are stored securely and never shared.
                                </p>
                            </form>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
